import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { warning } from '../../../../../components/Alert/Alert';
import Button from '../../../../../components/Button/Button';
import Checkbox from '../../../../../components/Checkbox/Checkbox';
import Input from '../../../../../components/Input/Input';
import { SelectCreate } from '../../../../../components/SelectCreate/SelectCreate';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import FormikMaterial from '../../../../MACRA_1/components/InputForm/Materials/ModalMaterial/FormikMaterial';
import { IMaterialResponse } from '../../../../api/IApiResponse';
import { conversionLabel } from '../../../../conversion';
import { UnitOfMeasure } from '../../../../enum/project';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { selectProject } from '../../../selector/conversion';
import { setStillingBasin } from '../../../store/project/projectSlice';
import { cleanResults } from '../../../store/project/resultsSlice';
import { EElevation, ISEElevation } from '../../graphicalResults/crossSection/enum/common';
import { ELiningThickness, ELiningThicknessImp } from '../../graphicalResults/crossSection/slopedWeir/ISlopedWeir';
import { IWeirGeometryVerticalDropForm } from '../WeirGeometry/IWeirGeometry';
import { IStillingBasinProps, IStillingBasinVerticalDropForm } from './IStillingBasin';

const counterweirElevationOptions = [
    { value: 0.3, label: '0.3' },
    { value: 0.5, label: '0.5' },
    { value: 1, label: '1' },
    { value: 1.5, label: '1.5' },
];

const elevationOptionsImp = [
    { value: ISEElevation.one, label: '1' },
    { value: ISEElevation.oneAndHalf, label: '1.5' },
    { value: ISEElevation.three, label: '3' },
    { value: ISEElevation.fourAndHalf, label: '4.5' },
];
const thicknessOptions = [
    { value: ELiningThickness.zeroPointSeventeen, label: '0.17' },
    { value: ELiningThickness.zeroPointTwentyThree, label: '0.23' },
    { value: ELiningThickness.zeroPointThirty, label: '0.30' },
    { value: ELiningThickness.zeroPointFive, label: '0.5' },
    { value: ELiningThickness.zeroPointSix, label: '0.6' },
    { value: ELiningThickness.one, label: '1' },
];
const thicknessOptionsImp = [
    { value: ELiningThicknessImp.zeroPointFiftyFive, label: '0.55' },
    { value: ELiningThicknessImp.zeroPointSeventyFive, label: '0.75' },
    { value: ELiningThicknessImp.one, label: '1' },
    { value: ELiningThicknessImp.oneAndHalf, label: '1.5' },
    { value: ELiningThicknessImp.three, label: '3' }
];
const StillingBasinVerticalDrop = (props: IStillingBasinProps) => {
    let timer = useRef<NodeJS.Timeout | null>(null);

    const dispatch = useAppDispatch();

    const { t } = useTranslation();
    const project = useAppSelector(selectProject);
    const projectInfo = useAppSelector(selectProjectInfo);
    let isImperial = projectInfo?.data?.unitOfMeasure == UnitOfMeasure.Imperial;

    const defaultValue: IStillingBasinVerticalDropForm = {
        isCounterweir: project.data!.stillingBasin!.isCounterweir,
        counterweirElevation: project.data!.stillingBasin!.counterweirElevation,
        poolLength: project.data!.stillingBasin!.poolLength,
        isStillingBasin: project.data!.stillingBasin!.isStillingBasin,
        width: project.data!.stillingBasin!.width,
        stillingBasinElevation: project.data!.stillingBasin!.stillingBasinElevation,
        thickness: project.data!.stillingBasin!.thickness,
        material: project.data!.stillingBasin!.material,
    };

    const [form, setForm] = useState<IStillingBasinVerticalDropForm>(defaultValue);
    const [openMaterials, setOpenMaterials] = useState<boolean>(false);
    const [disableStillingBasin, setDisableStillingBasin] = useState<boolean>(!defaultValue.isCounterweir!);
    const [disableBtnMaterial, setDisableBtnMaterial] = useState<boolean>(!defaultValue.isStillingBasin!);
    const [disableStyle, setDisableStyle] = useState<string>('disable');

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let name = e.target.name;
        let value = e.target.value ? parseFloat(e.target.value) : e.target.value;

        if (name == 'stillingBasinElevation') {
            let _foundation = (project.data!.weirGeometry as IWeirGeometryVerticalDropForm).foundation;
            if ((value as number) > 0) {
                value = 0;
                warning(t('warning_not_be_higher', { value: 0 }));
            } else if ((value as number) < 0) {
                let _counterweirElevation = 0;
                if (Math.abs(value as number) >= _foundation - form.thickness!) {
                    value = -(_foundation - form.thickness!);
                    if (value == 0) {
                        _counterweirElevation = EElevation.zeroPointFive;
                    }
                }
                return setForm({
                    ...form!,
                    stillingBasinElevation: value as number,
                    counterweirElevation: _counterweirElevation,
                });
            } else if (value == 0) {
                let _thickness = form.thickness;
                if (_thickness! >= _foundation) {
                    _thickness = _foundation;
                }
                return setForm({
                    ...form!,
                    counterweirElevation: EElevation.zeroPointFive,
                    stillingBasinElevation: value,
                    thickness: _thickness,
                });
            }
        }

        setForm({
            ...form!,
            [name]: value,
        });
    };

    const handleCounterweirChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm({
            ...form!,
            isCounterweir: e.target.checked,
            isStillingBasin: false,
        });
        setDisableStillingBasin(!disableStillingBasin);
    };

    const handleStillingBasinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm({
            ...form!,
            isStillingBasin: e.target.checked,
        });
        setDisableStyle(disableBtnMaterial ? '' : 'disable');
        setDisableBtnMaterial(!disableBtnMaterial);
    };

    useEffect(() => {
        if (timer.current) {
            clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
            dispatch(cleanResults());
            dispatch(setStillingBasin(form as IStillingBasinVerticalDropForm));
        }, 1000);
    }, [form]);

    const openModalMaterials = (e: React.MouseEvent<HTMLButtonElement>) => {
        setOpenMaterials(true);
    };
    const updateMaterial = (data: IMaterialResponse) => {
        setForm({ ...form, material: data });
    };

    return (
        <div className='d-flex flex-column gap-2'>
            <Checkbox title='Counterweir' name='isCounterweir' checked={form.isCounterweir} onChange={handleCounterweirChange} />
            <div className='d-flex justify-content-between align-items-center'>
                <label htmlFor='counterweirElevation'> {t('input_Elevation', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}</label>
                <SelectCreate
                    defaultValue={form.counterweirElevation}
                    name='counterweirElevation'
                    options={isImperial ? elevationOptionsImp : counterweirElevationOptions}
                    parentOnChange={(newValue: any) =>
                        setForm({
                            ...form!,
                            counterweirElevation: newValue,
                        })
                    }
                />
            </div>
            <Input
                type='number'
                title={t('input_basinLength', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                value={form.poolLength}
                name='poolLength'
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputChange}
            />
            <Input
                type='number'
                title={t('input_basinWidth', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                name='width'
                value={form.width}
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputChange}
            />

            <Checkbox title='Lined basin' checked={form.isStillingBasin} name='isStillingBasin' disabled={disableStillingBasin} onChange={handleStillingBasinChange} />

            <Input
                type='number'
                title={t('input_Elevation', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                value={form.stillingBasinElevation}
                name='stillingBasinElevation'
                className='d-flex justify-content-between align-items-center'
                disabled={disableStillingBasin}
                onChange={handleInputChange}
            />
            <div className='d-flex justify-content-between align-items-center'>
                <label htmlFor='thickness'>{t('input_thickness', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}</label>
                <SelectCreate
                    value={form.thickness}
                    name='thickness'
                    options={isImperial ? thicknessOptionsImp : thicknessOptions}
                    disabled={disableStillingBasin}
                    parentOnChange={(newValue: any) =>
                        setForm({
                            ...form!,
                            thickness: newValue,
                        })
                    }
                />
            </div>
            <div className='d-flex justify-content-center align-items-center mb-2'>
                <Button classNameBtn={`btn-materials ${disableStyle}`} disabled={disableBtnMaterial} onClick={openModalMaterials}>
                    {t('material_bed_material_button')}
                </Button>

                {openMaterials && (
                    <FormikMaterial
                        material={form.material}
                        isMacra2
                        open={openMaterials}
                        materialId={form.material?.id}
                        closeModal={() => setOpenMaterials(false)}
                        onClose={data => updateMaterial(data!)}
                    />
                )}
            </div>
        </div>
    );
};

export default StillingBasinVerticalDrop;
